import { useState, useEffect, useRef, useCallback } from 'react';
import { cropApi } from '@/services/cropApi';

interface DeviceSettingsProps {
  deviceId: string;
  housingHeightMm: number;
}

interface PollSettings {
  rgb1_angle: number;
  rgb2_angle: number;
  timer1_time?: string;
  timer1_duration?: number;
  timer2_time?: string;
  timer2_duration?: number;
}

export default function DeviceSettings({ deviceId, housingHeightMm }: DeviceSettingsProps) {
  const [housingHeight, setHousingHeight] = useState(String(housingHeightMm));
  const [timer1Time, setTimer1Time] = useState('06:30');
  const [timer1Duration, setTimer1Duration] = useState('10');
  const [timer2Time, setTimer2Time] = useState('18:30');
  const [timer2Duration, setTimer2Duration] = useState('10');
  const [rgb1InitAngle, setRgb1InitAngle] = useState('90');
  const [rgb2InitAngle, setRgb2InitAngle] = useState('90');
  const [sending, setSending] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [toastMsg, setToastMsg] = useState('');
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const toastTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const cancelledRef = useRef(false);

  const showToast = useCallback((msg: string, type: 'success' | 'error' = 'success') => {
    setToastMsg(msg);
    setToastType(type);
    if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    toastTimerRef.current = setTimeout(() => setToastMsg(''), 3000);
  }, []);

  // Sync housing height from props
  useEffect(() => {
    setHousingHeight(String(housingHeightMm));
  }, [housingHeightMm]);

  // Initial load from api_servo_poll.php
  useEffect(() => {
    if (!deviceId) return;
    cancelledRef.current = false;

    (async () => {
      try {
        const res = await fetch(
          `https://tof.eemo.co.kr/api/api_servo_poll.php?device_id=${deviceId}&after_id=0`,
        );
        const data: PollSettings & { result: string } = await res.json();
        if (cancelledRef.current || data.result !== 'success') return;

        if (typeof data.rgb1_angle === 'number') setRgb1InitAngle(String(data.rgb1_angle));
        if (typeof data.rgb2_angle === 'number') setRgb2InitAngle(String(data.rgb2_angle));
        if (data.timer1_time) setTimer1Time(data.timer1_time);
        if (typeof data.timer1_duration === 'number') setTimer1Duration(String(data.timer1_duration));
        if (data.timer2_time) setTimer2Time(data.timer2_time);
        if (typeof data.timer2_duration === 'number') setTimer2Duration(String(data.timer2_duration));
      } catch {
        // ignore init errors — use defaults
      }
    })();

    return () => {
      cancelledRef.current = true;
      if (toastTimerRef.current) clearTimeout(toastTimerRef.current);
    };
  }, [deviceId]);

  const handleSend = useCallback(async () => {
    setShowConfirm(false);
    setSending(true);
    try {
      const result = await cropApi.sendSettings({
        deviceId,
        timer1Time,
        timer1Duration: Number(timer1Duration),
        timer2Time,
        timer2Duration: Number(timer2Duration),
        housingHeight: Number(housingHeight),
        rgb1InitAngle: Number(rgb1InitAngle),
        rgb2InitAngle: Number(rgb2InitAngle),
      });
      if (result.result === 'success') {
        showToast('설정이 전송되었습니다 ✓', 'success');
      } else {
        showToast('설정 전송 실패', 'error');
      }
    } catch {
      showToast('설정 전송 중 오류가 발생했습니다.', 'error');
    } finally {
      setSending(false);
    }
  }, [deviceId, timer1Time, timer1Duration, timer2Time, timer2Duration, housingHeight, rgb1InitAngle, rgb2InitAngle, showToast]);

  return (
    <>
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
        {/* Header */}
        <div
          className="px-5 py-3.5 border-b border-gray-100 flex items-center gap-2"
          style={{ backgroundColor: '#fef9f0' }}
        >
          <div className="w-5 h-5 flex items-center justify-center">
            <i className="ri-settings-3-line text-amber-600"></i>
          </div>
          <span className="font-bold text-gray-800 text-sm">장비 원격 설정</span>
        </div>

        <div className="p-4 md:p-5 space-y-4">
          {/* Row 1: Timer Settings */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Timer 1 */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-amber-50">
                  <i className="ri-timer-line text-amber-600 text-sm"></i>
                </div>
                타이머 1
              </h4>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">촬영 시각</label>
                  <input
                    type="time"
                    value={timer1Time}
                    onChange={(e) => setTimer1Time(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 bg-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">활성 시간</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={120}
                      value={timer1Duration}
                      onChange={(e) => setTimer1Duration(e.target.value)}
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 bg-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">분</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Timer 2 */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-amber-50">
                  <i className="ri-timer-line text-amber-600 text-sm"></i>
                </div>
                타이머 2
              </h4>
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">촬영 시각</label>
                  <input
                    type="time"
                    value={timer2Time}
                    onChange={(e) => setTimer2Time(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 bg-white"
                  />
                </div>
                <div className="flex-1">
                  <label className="block text-xs text-gray-500 mb-1">활성 시간</label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={120}
                      value={timer2Duration}
                      onChange={(e) => setTimer2Duration(e.target.value)}
                      className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-amber-300 focus:border-amber-300 bg-white"
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">분</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Row 2: Other Settings */}
          <div className="flex flex-col md:flex-row gap-4">
            {/* Housing Height */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-green-50">
                  <i className="ri-ruler-line text-green-600 text-sm"></i>
                </div>
                하우징 높이
              </h4>
              <div className="relative">
                <input
                  type="number"
                  min={500}
                  max={5000}
                  value={housingHeight}
                  onChange={(e) => setHousingHeight(e.target.value)}
                  className="w-full px-3 py-2 pr-12 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-300 bg-white"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">mm</span>
              </div>
            </div>

            {/* RGB1 Initial Angle */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-green-50">
                  <i className="ri-camera-line text-green-600 text-sm"></i>
                </div>
                RGB1 초기각도
              </h4>
              <div className="relative">
                <input
                  type="number"
                  min={60}
                  max={120}
                  value={rgb1InitAngle}
                  onChange={(e) => setRgb1InitAngle(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-green-300 focus:border-green-300 bg-white"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">°</span>
              </div>
            </div>

            {/* RGB2 Initial Angle */}
            <div className="flex-1 bg-gray-50 rounded-xl p-4">
              <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                <div className="w-6 h-6 flex items-center justify-center rounded-md bg-orange-50">
                  <i className="ri-camera-line text-orange-500 text-sm"></i>
                </div>
                RGB2 초기각도
              </h4>
              <div className="relative">
                <input
                  type="number"
                  min={60}
                  max={120}
                  value={rgb2InitAngle}
                  onChange={(e) => setRgb2InitAngle(e.target.value)}
                  className="w-full px-3 py-2 pr-10 rounded-lg border border-gray-200 text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 bg-white"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">°</span>
              </div>
            </div>
          </div>

          {/* Action Row */}
          <div className="flex flex-col items-center gap-2 pt-2">
            <button
              onClick={() => setShowConfirm(true)}
              disabled={sending}
              className="px-8 py-2.5 rounded-xl font-semibold text-sm text-white cursor-pointer whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed hover:opacity-90"
              style={{ backgroundColor: '#27ae60' }}
            >
              {sending ? (
                <span className="inline-flex items-center gap-2">
                  <div className="w-4 h-4 flex items-center justify-center">
                    <i className="ri-loader-4-line animate-spin"></i>
                  </div>
                  전송중...
                </span>
              ) : (
                '설정 전송'
              )}
            </button>
            <p className="text-xs text-gray-400 flex items-center gap-1">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-information-line"></i>
              </div>
              장비가 활성 상태일 때만 설정이 적용됩니다
            </p>
          </div>
        </div>
      </div>

      {/* Confirmation Dialog */}
      {showConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-2xl max-w-sm w-full mx-4 shadow-xl">
            <div className="p-5 border-b border-gray-100">
              <h3 className="text-base font-bold text-gray-800">설정 전송 확인</h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 mb-2">다음 설정을 장비에 전송합니다:</p>
              <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">타이머1</span>
                  <span className="text-gray-800 font-medium">{timer1Time} ({timer1Duration}분간 활성)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">타이머2</span>
                  <span className="text-gray-800 font-medium">{timer2Time} ({timer2Duration}분간 활성)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">하우징 높이</span>
                  <span className="text-gray-800 font-medium">{housingHeight}mm</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">RGB1 초기각도</span>
                  <span className="text-gray-800 font-medium">{rgb1InitAngle}°</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">RGB2 초기각도</span>
                  <span className="text-gray-800 font-medium">{rgb2InitAngle}°</span>
                </div>
              </div>
              <p className="text-xs text-amber-600 flex items-center gap-1">
                <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                  <i className="ri-error-warning-line"></i>
                </div>
                장비가 활성 상태일 때만 적용됩니다.
              </p>
            </div>
            <div className="p-4 border-t border-gray-100 flex gap-3 justify-end">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-5 py-2 rounded-xl text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 cursor-pointer whitespace-nowrap transition-all"
              >
                취소
              </button>
              <button
                onClick={handleSend}
                className="px-5 py-2 rounded-xl text-sm font-semibold text-white cursor-pointer whitespace-nowrap transition-all hover:opacity-90"
                style={{ backgroundColor: '#27ae60' }}
              >
                전송
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toastMsg && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-5 py-2.5 rounded-xl shadow-lg text-sm whitespace-nowrap animate-pulse"
          style={{ backgroundColor: toastType === 'success' ? '#14532d' : '#991b1b' }}
        >
          <span className="text-white">{toastMsg}</span>
        </div>
      )}
    </>
  );
}